import React, { useState } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import { storage, db } from '../../firebase';
import { doc, setDoc, collection, getDocs, getDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./HotelRoomUpload.css";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const HotelRoomUpload = () => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [p1, setP1] = useState('');
  const [price, setPrice] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState('');
  const [images, setImages] = useState([]); // State for multiple images
  const [iconImage, setIconImage] = useState(null); // State for icon image
  const [editRoomId, setEditRoomId] = useState(null); // State to track the room being edited
  const [rooms, loading, error] = useCollection(collection(db, 'Rooms'));

  const handleImageChange = (e) => {
    if (e.target.files.length > 0) {
      setImages([...images, ...e.target.files]);
    }
  };

  const handleIconImageChange = (e) => {
    if (e.target.files.length > 0) {
      setIconImage(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!title || !content || !p1 || !price || !discountedPrice || (!images.length && !editRoomId) || (!iconImage && !editRoomId)) {
      toast.error('Please fill in all fields');
      return;
    }

    toast.info('Processing...', { autoClose: 3000 });

    try {
      let imageUrls = [];
      let iconImageUrl;

      if (images.length > 0) {
        for (const image of images) {
          const imageRef = ref(storage, `Room_images/${image.name}`);
          await uploadBytes(imageRef, image);
          const imageUrl = await getDownloadURL(imageRef);
          imageUrls.push(imageUrl);
        }
      }

      if (iconImage) {
        const iconImageRef = ref(storage, `Room_icons/${iconImage.name}`);
        await uploadBytes(iconImageRef, iconImage);
        iconImageUrl = await getDownloadURL(iconImageRef);
      }

      const timestamp = new Date();
      const roomRef = editRoomId ? doc(db, 'Rooms', editRoomId) : doc(collection(db, 'Rooms'));

      await setDoc(roomRef, {
        title,
        content,
        p1,
        price,
        discountedPrice,
        imageUrls: imageUrls.length > 0 ? imageUrls : (await getDoc(roomRef)).data()?.imageUrls,
        iconImageUrl: iconImageUrl || (await getDoc(roomRef)).data()?.iconImageUrl,
        timestamp,
      });

      toast.success(editRoomId ? 'Room updated successfully!' : 'Room uploaded successfully!');
      setTitle('');
      setContent('');
      setP1('');
      setPrice('');
      setDiscountedPrice('');
      setImages([]);
      setIconImage(null);
      setEditRoomId(null);

    } catch (error) {
      console.error('Error processing Room:', error);
      toast.error('Error processing Room');
    }
  };

  const handleEdit = (room) => {
    setTitle(room.title);
    setContent(room.content);
    setP1(room.p1);
    setPrice(room.price);
    setDiscountedPrice(room.discountedPrice);
    setEditRoomId(room.id);
  };

  const handleDelete = async (roomId, imageUrls, iconImageUrl) => {
    try {
      const roomRef = doc(db, 'Rooms', roomId);

      // Delete the document from Firestore
      await deleteDoc(roomRef);

      // Delete the images from storage
      imageUrls.forEach(async (imageUrl) => {
        const imageRef = ref(storage, `Room_images/${imageUrl}`);
        await deleteObject(imageRef);
      });

      if (iconImageUrl) {
        const iconImageRef = ref(storage, `Room_icons/${iconImageUrl}`);
        await deleteObject(iconImageRef);
      }

      toast.success('Room deleted successfully!');
    } catch (error) {
      console.error('Error deleting Room:', error);
      toast.error('Error deleting Room');
    }
  };

  return (
    <div className='mainRoomUpload'>
      <div className='mainUploadRoom'>
        <div className='uploadRoom1'>
          <h2>{editRoomId ? 'Edit Room' : 'Upload Room'}</h2>
          <div className='uploadRoom'>
            <label>Title:</label>
            <input type='text' value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className='uploadRoom'>
            <label>Description</label>
            <input type='text' value={p1} onChange={(e) => setP1(e.target.value)} />
          </div>

          <div className='uploadRoom'>
            <label>Price:</label>
            <input type='number' value={price} onChange={(e) => setPrice(e.target.value)} />
          </div>

          <div className='uploadRoom'>
            <label>Discounted Price:</label>
            <input type='number' value={discountedPrice} onChange={(e) => setDiscountedPrice(e.target.value)} />
          </div>

          <div className='uploadRoom'>
            <label>Main Images:</label>
            <input type='file' multiple onChange={handleImageChange} />
            <div className="image-preview">
              {images.map((image, index) => (
                <img key={index} src={URL.createObjectURL(image)} alt={`preview ${index}`} className="preview-image" />
              ))}
            </div>
          </div>

          <div className='uploadRoom'>
            <label>Icon Image:</label>
            <input type='file' onChange={handleIconImageChange} />
          </div>

          <div className='uploadRoom'>
            <label>Content:</label>
            <ReactQuill
              value={content}
              onChange={(value) => setContent(value)}
              modules={{
                toolbar: {
                  container: [
                    [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
                    [{ size: [] }],
                    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                    [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                    ['link'],
                    ['clean']
                  ],
                },
              }}
              formats={[
                'header', 'font', 'size',
                'bold', 'italic', 'underline', 'strike', 'blockquote',
                'list', 'bullet', 'indent',
                'link'
              ]}
              className="custom-quill"
            />
          </div>

          <button className='uploadBtn' onClick={handleUpload}>
            {editRoomId ? 'Update' : 'Upload'}
          </button>
        </div>
        <hr />

        <h2>Room List</h2>
        {loading && <p>Loading...</p>}
        {error && <p>Error fetching Rooms</p>}
        {rooms && (
          <div className='roomList'>
            {rooms.docs.map((room) => {
              const data = room.data();
              return (
                <div key={room.id} className='roomcard-1'>
                  <img src={data.iconImageUrl} alt="Icon" className="icon-img" />
                  <img src={data.imageUrls[0]} alt={data.title} style={{ maxWidth: '100%' }} />
                  <p className='time'>
                    {data.timestamp &&
                      new Date(data.timestamp.toDate()).toLocaleString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                  </p>
                  <h3 className='roomTitle'>{data.title}</h3>
                  <p className='roomDesc'>{data.p1}</p>
                  <p className='roomPrice'>Price: ?{data.price}</p>
                  <p className='roomDiscountedPrice'>Discounted Price: ?{data.discountedPrice}</p>
                  <button className='editBtn' onClick={() => handleEdit({ id: room.id, ...data })}>Edit</button>
                  <button className='deleteBtn' onClick={() => handleDelete(room.id, data.imageUrls, data.iconImageUrl)}>Delete</button>
                </div>
              );
            })}
          </div>
        )}

        <ToastContainer />
      </div>
    </div>
  );
};
export default HotelRoomUpload;